/**
 * Account Restriction Utilities
 * Handles logic for restricting accounts based on approvalRestricted purpose codes
 */

/**
 * Computes account restrictions based on approvalRestricted purpose codes
 * @param {Object} consent - The consent object with Purpose.code
 * @param {Array} accounts - Array of account objects
 * @returns {Object} Restriction map with accRefNumber as key and restriction details as value
 */
export const computeAccountRestrictions = (consent, accounts) => {
	const restrictionMap = {};
	const currentPurposeCode = consent?.Purpose?.code;
	
	if (!currentPurposeCode || !accounts) {
		return restrictionMap;
	}

	accounts.forEach((account) => {
		const accountRestrictions = account?.approvalRestricted?.purposeCode;
		const isRestricted = accountRestrictions?.includes(currentPurposeCode);
		
		if (isRestricted) {
			restrictionMap[account.accRefNumber] = {
				isRestricted: true,
				reason: `You have already provided consent for this account for the given purpose.`
			};
		}
	});

	return restrictionMap;
};

/**
 * Filters out restricted accounts from a list of accounts
 * @param {Array} accounts - Array of account objects
 * @param {Array} consents - Array of consent objects
 * @returns {Array} Filtered accounts without restrictions
 */
export const filterRestrictedAccounts = (accounts, consents) => {
	if (!accounts || !consents) return accounts;
	
	return accounts.filter(account => {
		// Check if account is restricted for any consent
		return !consents.some(consent => {
			const restrictions = computeAccountRestrictions(consent, [account]);
			return restrictions[account.accRefNumber]?.isRestricted;
		});
	});
};

/**
 * Checks if an account is restricted for a specific consent
 * @param {Object} account - Account object
 * @param {Object} consent - Consent object
 * @returns {boolean} True if account is restricted for the consent
 */
export const isAccountRestrictedForConsent = (account, consent) => {
	if (!account || !consent) return false;
	
	const restrictions = computeAccountRestrictions(consent, [account]);
	return restrictions[account.accRefNumber]?.isRestricted || false;
};

/**
 * Removes restricted accounts from selected accounts
 * @param {Array} selectedAccounts - Array of selected account objects
 * @param {Object} consentSelectedAccounts - Object with consent handle as key and accounts as value
 * @param {Array} consents - Array of consent objects
 * @param {Array} allAccounts - Array of all account objects
 * @returns {Object} Updated selected accounts and consent selected accounts
 */
export const removeRestrictedAccountsFromSelection = (selectedAccounts, consentSelectedAccounts, consents, allAccounts) => {
	const updatedSelectedAccounts = [...selectedAccounts];
	const updatedConsentSelectedAccounts = { ...consentSelectedAccounts };
	
	consents.forEach((consent) => {
		const restrictions = computeAccountRestrictions(consent, allAccounts);
		const restrictedAccountRefs = Object.keys(restrictions);
		
		if (restrictedAccountRefs.length > 0) {
			// Remove restricted accounts from selected accounts
			const filteredSelectedAccounts = updatedSelectedAccounts.filter(
				(account) => !restrictedAccountRefs.includes(account.accRefNumber)
			);
			
			// Remove restricted accounts from consent selected accounts
			if (updatedConsentSelectedAccounts[consent.ConsentHandle]) {
				updatedConsentSelectedAccounts[consent.ConsentHandle] = 
					updatedConsentSelectedAccounts[consent.ConsentHandle].filter(
						(account) => !restrictedAccountRefs.includes(account.accRefNumber)
					);
			}
			
			// Update the arrays
			updatedSelectedAccounts.length = 0;
			updatedSelectedAccounts.push(...filteredSelectedAccounts);
		}
	});
	
	return {
		selectedAccounts: updatedSelectedAccounts,
		consentSelectedAccounts: updatedConsentSelectedAccounts
	};
};

/**
 * Calculates account blocking analytics metrics
 * @param {Array} linkedAccounts - Array of linked account objects
 * @param {Array} consents - Array of consent objects
 * @returns {Object} Analytics metrics for account blocking
 */
export const calculateAccountBlockingMetrics = (linkedAccounts, consents) => {
	if (!linkedAccounts || !consents) {
		return {
			totalLinkedAccounts: 0,
			totalBlockedAccounts: 0,
			blockedAccountsPercentage: 0,
			totalConsents: 0,
			hasBlockedAccounts: false,
			blockedPurposeCodes: [],
		};
	}

	// Calculate total linked accounts
	const totalLinkedAccounts = linkedAccounts.length;
	const totalConsents = consents.length;

	// Calculate blocked accounts across all consents and track purpose codes
	let totalBlockedAccounts = 0;
	const blockedAccountRefs = new Set();
	const blockedPurposeCodes = new Set(); // Set to track unique purpose codes that are blocked

	consents.forEach((consent) => {
		const currentPurposeCode = consent?.Purpose?.code;
		const restrictions = computeAccountRestrictions(consent, linkedAccounts);
		
		Object.keys(restrictions).forEach((accRefNumber) => {
			blockedAccountRefs.add(accRefNumber);
			
			// Track purpose codes that are blocked
			if (currentPurposeCode) {
				blockedPurposeCodes.add(currentPurposeCode);
			}
		});
	});

	totalBlockedAccounts = blockedAccountRefs.size;

	// Convert Set to array for analytics
	const blockedPurposeCodesArray = Array.from(blockedPurposeCodes);

	return {
		totalLinkedAccounts,
		totalBlockedAccounts,
		blockedAccountsPercentage: totalLinkedAccounts > 0 ? (totalBlockedAccounts / totalLinkedAccounts) * 100 : 0,
		totalConsents,
		hasBlockedAccounts: totalBlockedAccounts > 0,
		blockedPurposeCodes: blockedPurposeCodesArray,
	};
}; 